/**
 * Aprendizaje del parser — métricas por cliente/campo, reglas explícitas (confirmar/borrar/añadir),
 * ejemplos aprobados y reglas globales del sector. Nada se aprende sin confirmación humana.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Brain, Check, Trash2, RotateCcw, Eye } from "lucide-react";
import ClienteCombobox from "@/components/ClienteCombobox";
import { useAuth } from "@/context/AuthContext";

const CAMPOS = ["tipo_trabajo", "espacio", "componentes", "cantidades_medidas", "fechas_permisos", "cliente"];
const TEND = { mejora: ["↑ mejora", "text-emerald-300"], empeora: ["↓ empeora", "text-rose-300"], estable: ["= estable", "text-zinc-400"] };

export default function AprendizajeParser() {
  const [glob, setGlob] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [cid, setCid] = useState(null);
  const [perfil, setPerfil] = useState(null);
  const [contexto, setContexto] = useState(null);
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).includes("admin");

  const loadGlob = () => api.get("/parser-learning/metricas").then((r) => setGlob(r.data));
  const loadPerfil = (id) => id && api.get(`/parser-learning/perfil/${id}`).then((r) => setPerfil(r.data));
  useEffect(() => { loadGlob(); api.get("/clientes").then((r) => setClientes(Array.isArray(r.data) ? r.data : r.data.items || [])); }, []);
  useEffect(() => { setContexto(null); loadPerfil(cid); }, [cid]);

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="aprendizaje-page">
      <h1 className="text-xl font-semibold flex items-center gap-2"><Brain className="w-5 h-5 text-violet-400" /> Aprendizaje del parser de correos</h1>
      <p className="text-xs text-zinc-400 max-w-3xl">Cada borrador aprobado compara lo que propuso la IA con lo que confirmó el validador. Las métricas y ejemplos salen sólo de datos confirmados; las reglas aprendidas quedan <b>inactivas</b> hasta que alguien las confirme.</p>

      <div className="grid md:grid-cols-[1fr_2fr] gap-4">
        <div className="space-y-2">
          <div className="text-xs text-zinc-400">Clientes con aprendizaje · {glob?.total_feedback ?? 0} correcciones registradas</div>
          {(glob?.clientes || []).map((c) => (
            <button key={c.cliente_id} onClick={() => setCid(c.cliente_id)} data-testid={`aprendizaje-cliente-${c.cliente_id}`}
              className={`w-full text-left rounded-lg border p-2 text-sm ${cid === c.cliente_id ? "border-violet-500/50 bg-violet-500/5" : "border-zinc-800 hover:bg-zinc-900/60"}`}>
              <div className="font-medium">{c.cliente_nombre || c.cliente_id}</div>
              <div className="text-[11px] text-zinc-400">{c.n_feedback} aprobaciones · {c.reglas_activas} reglas activas{c.reglas_sugeridas ? ` · ${c.reglas_sugeridas} sugeridas` : ""}</div>
            </button>
          ))}
          <div className="pt-2 text-xs text-zinc-400">…o elige cualquier cliente:</div>
          <ClienteCombobox value={cid} clientes={clientes} onChange={(id) => setCid(id || null)} testId="aprendizaje-cliente-combobox" />
          <GlobalRules />
        </div>
        <div>
          {!perfil && <div className="text-sm text-zinc-500 border border-dashed border-zinc-800 rounded p-6 text-center">Selecciona un cliente.</div>}
          {perfil && (
            <div className="space-y-4" data-testid="aprendizaje-perfil">
              <Metricas m={perfil.metricas_resumen} />
              <Reglas cid={cid} reglas={perfil.reglas || []} reload={() => loadPerfil(cid)} />
              <Ejemplos ejemplos={perfil.ejemplos || []} feedback={perfil.feedback_reciente || []} />
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" data-testid="aprendizaje-ver-contexto" onClick={() => api.get(`/parser-learning/perfil/${cid}/contexto`).then((r) => setContexto(r.data.contexto || "(vacío: sin reglas ni ejemplos aún)"))}><Eye className="w-3.5 h-3.5 mr-1" />Ver qué lee la IA</Button>
                {isAdmin && <Button size="sm" variant="ghost" className="text-rose-300" data-testid="aprendizaje-reset" onClick={async () => {
                  if (!window.confirm("¿Borrar TODO el aprendizaje de este cliente (reglas, ejemplos, métricas y feedback)?")) return;
                  await api.delete(`/parser-learning/perfil/${cid}`); toast.success("Aprendizaje reseteado"); loadPerfil(cid); loadGlob();
                }}><RotateCcw className="w-3.5 h-3.5 mr-1" />Resetear aprendizaje</Button>}
              </div>
              {contexto !== null && <pre className="text-[11px] whitespace-pre-wrap bg-zinc-900/60 rounded p-3 max-h-72 overflow-auto" data-testid="aprendizaje-contexto">{contexto}</pre>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Metricas({ m }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="aprendizaje-metricas">
      {CAMPOS.map((k) => {
        const x = m?.[k] || {};
        const tot = (x.aciertos || 0) + (x.correcciones || 0);
        const [tl, tc] = TEND[x.tendencia] || ["—", "text-zinc-500"];
        const pct = x.tasa != null ? Math.round(x.tasa * 100) : null;
        return (
          <div key={k} className="rounded-lg border border-zinc-800 p-2" data-testid={`metrica-${k}`}>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">{x.label || k}</div>
            <div className="text-lg font-semibold">{pct == null ? "—" : `${pct}%`} <span className="text-[11px] font-normal text-zinc-400">aciertos</span></div>
            <div className="text-[11px] text-zinc-400">{x.aciertos || 0} ok · {x.correcciones || 0} corregidos · n={tot} · <span className={tc}>{tl}</span></div>
            <div className="mt-1 h-1.5 rounded bg-zinc-800 overflow-hidden"><div className={`h-full ${pct >= 75 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${pct || 0}%` }} /></div>
          </div>
        );
      })}
    </div>
  );
}

function Reglas({ cid, reglas, reload }) {
  const [texto, setTexto] = useState("");
  const [campo, setCampo] = useState("tipo_trabajo");
  const sug = reglas.filter((r) => !r.activa);
  const act = reglas.filter((r) => r.activa);
  const add = async () => { await api.post(`/parser-learning/perfil/${cid}/reglas`, { campo, texto }); setTexto(""); toast.success("Regla añadida"); reload(); };
  return (
    <div className="space-y-2" data-testid="aprendizaje-reglas">
      {sug.length > 0 && (
        <div className="rounded-lg border border-amber-500/30 p-2 space-y-1">
          <div className="text-xs font-medium text-amber-200">Reglas sugeridas por repetición (inactivas hasta confirmar)</div>
          {sug.map((r) => (
            <div key={r.id} className="flex items-start gap-2 text-xs" data-testid={`regla-sugerida-${r.id}`}>
              <span className="flex-1"><b>{r.campo}</b> · {r.texto} <span className="text-zinc-500">({r.evidencias} evidencias)</span></span>
              <Button size="sm" variant="outline" className="h-6 px-2" data-testid={`regla-confirmar-${r.id}`} onClick={() => api.post(`/parser-learning/perfil/${cid}/reglas/${r.id}/confirmar`, {}).then(() => { toast.success("Regla confirmada"); reload(); })}><Check className="w-3 h-3" /></Button>
              <button className="text-rose-300" data-testid={`regla-borrar-${r.id}`} onClick={() => api.delete(`/parser-learning/perfil/${cid}/reglas/${r.id}`).then(reload)}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
      <div className="text-xs font-medium">Reglas activas del cliente ({act.length})</div>
      {act.map((r) => (
        <div key={r.id} className="flex items-start gap-2 text-xs rounded border border-zinc-800 p-2" data-testid={`regla-${r.id}`}>
          <span className="flex-1"><b>{r.campo}</b> · {r.texto} <span className="text-zinc-500">· {r.origen}{r.confirmada_por ? ` · ${r.confirmada_por}` : ""}</span></span>
          <button className="text-rose-300" data-testid={`regla-borrar-${r.id}`} onClick={() => api.delete(`/parser-learning/perfil/${cid}/reglas/${r.id}`).then(reload)}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <div className="flex gap-2 items-center">
        <select value={campo} onChange={(e) => setCampo(e.target.value)} className="h-8 text-xs bg-zinc-900 border border-zinc-700 rounded px-1" data-testid="regla-campo">{CAMPOS.map((c) => <option key={c} value={c}>{c}</option>)}</select>
        <Input value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Nueva regla explícita para este cliente…" className="h-8 text-xs" data-testid="regla-texto" />
        <Button size="sm" disabled={!texto.trim()} onClick={add} data-testid="regla-add">Añadir</Button>
      </div>
    </div>
  );
}

function Ejemplos({ ejemplos, feedback }) {
  return (
    <div className="space-y-1" data-testid="aprendizaje-ejemplos">
      <div className="text-xs font-medium">Últimas aprobaciones (predicción vs corrección)</div>
      {feedback.length === 0 && <div className="text-xs text-zinc-500">Aún no hay aprobaciones para este cliente.</div>}
      {feedback.slice(0, 8).map((f) => (
        <div key={f.id} className="text-xs rounded border border-zinc-800 p-2" data-testid={`feedback-${f.id}`}>
          <div className="flex justify-between"><span className="font-medium truncate">{f.asunto || "(sin asunto)"}</span><span className={f.n_correcciones ? "text-amber-300" : "text-emerald-300"}>{f.n_correcciones ? `${f.n_correcciones} corrección(es)` : "sin correcciones"}</span></div>
          <div className="text-zinc-400">{(f.diffs || []).filter((d) => !d.ok).map((d) => `${d.campo}: ${JSON.stringify(d.antes)} → ${JSON.stringify(d.despues)}`).join(" · ") || "La IA acertó todos los campos"} · {(f.at || "").slice(0, 10)} · {f.validado_por}</div>
        </div>
      ))}
    </div>
  );
}

function GlobalRules() {
  const [g, setG] = useState({ reglas: [] });
  const [texto, setTexto] = useState("");
  const load = () => api.get("/parser-learning/global").then((r) => setG(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div className="rounded-lg border border-zinc-800 p-2 space-y-1 mt-3" data-testid="reglas-globales">
      <div className="text-xs font-medium">Reglas globales del sector (todos los clientes)</div>
      {(g.reglas || []).map((r) => (
        <div key={r.id} className="flex gap-2 text-xs" data-testid={`regla-global-${r.id}`}><span className="flex-1">{r.texto}</span>
          <button className="text-rose-300" data-testid={`regla-global-del-${r.id}`} onClick={() => api.delete(`/parser-learning/global/reglas/${r.id}`).then(load)}><Trash2 className="w-3.5 h-3.5" /></button></div>
      ))}
      <div className="flex gap-1"><Input value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Regla global…" className="h-7 text-xs" data-testid="regla-global-texto" />
        <Button size="sm" className="h-7" disabled={!texto.trim()} data-testid="regla-global-add" onClick={() => api.post("/parser-learning/global/reglas", { texto }).then(() => { setTexto(""); load(); })}>+</Button></div>
    </div>
  );
}
